import React, { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, AlertCircle, CheckCircle } from "lucide-react";
import { Dialog, DialogContent } from "./ui/Dialog";

/**
 * PWA Update Dialog - Centered confirmation dialog for available updates.
 * Provides Apply and Later actions with proper focus management.
 */
export default function PWAUpdateDialog({
  isOpen,
  onClose,
  onApply,
  onLater,
  isApplying = false,
}) {
  const { t } = useTranslation("common");
  const dialogRef = useRef(null);
  const previousActiveElement = useRef(null);
  const focusableElementsRef = useRef([]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      // Capture focusable elements after render
      setTimeout(() => {
        if (dialogRef.current) {
          focusableElementsRef.current = Array.from(
            dialogRef.current.querySelectorAll(
              'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
            )
          );
          const firstFocusable = focusableElementsRef.current[0];
          if (firstFocusable) firstFocusable.focus();
        }
      }, 0);
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus();
      previousActiveElement.current = null;
    }
  }, [isOpen]);

  // Keyboard handling
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    const focusableElements = focusableElementsRef.current;
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    switch (e.key) {
      case "Tab":
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
        break;
      case "Escape":
        e.preventDefault();
        onLater?.();
        break;
    }
  }, [isOpen, onLater]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        ref={dialogRef}
        className="dark:bg-neutral-900 dark:text-white max-w-md w-full mx-4"
        role="alertdialog"
        aria-labelledby="pwa-update-dialog-title"
        aria-describedby="pwa-update-dialog-description"
      >
        <div className="relative">
          <button
            type="button"
            onClick={onLater}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400"
            aria-label={t("common:close", "Close")}
          >
            <X size={20} aria-hidden="true" />
          </button>

          <div className="px-6 py-6 sm:py-8 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <AlertCircle size={28} className="text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            </div>

            <h2
              id="pwa-update-dialog-title"
              className="text-lg font-bold text-gray-900 dark:text-white mb-2"
            >
              {t("pwaUpdate.dialogTitle", "Update Available")}
            </h2>

            <p
              id="pwa-update-dialog-description"
              className="text-sm text-gray-600 dark:text-gray-400 mb-6"
            >
              {t("pwaUpdate.dialogMessage", "A new version of Orrith is ready. Update now to get the latest features and improvements.")}
            </p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={onApply}
                disabled={isApplying}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-green-950 dark:text-green-50 bg-emerald-400 dark:bg-emerald-500 rounded-xl hover:bg-emerald-300 dark:hover:bg-emerald-400 active:bg-emerald-500 dark:active:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-green-950 dark:focus:ring-offset-neutral-950"
                aria-label={isApplying ? t("pwaUpdate.applying", "Applying update...") : t("pwaUpdate.applyButton", "Apply Update")}
              >
                {isApplying ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>{t("pwaUpdate.applying", "Applying update...")}</span>
                  </>
                ) : (
                  <>
                    <Download size={18} aria-hidden="true" />
                    <span>{t("pwaUpdate.applyButton", "Apply Update")}</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={onLater}
                disabled={isApplying}
                className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-emerald-100 dark:text-emerald-50 bg-transparent hover:bg-emerald-600/20 dark:hover:bg-emerald-500/20 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-green-950 dark:focus:ring-offset-neutral-950 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t("pwaUpdate.laterButton", "Later")}
              >
                {t("pwaUpdate.laterButton", "Later")}
              </button>
            </div>

            <p className="mt-4 text-xs text-gray-500 dark:text-gray-500">
              {t("pwaUpdate.dialogNote", "Your current session will continue uninterrupted if you choose Later.")}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}